import { useMemo } from 'react';
import { TableIconButton } from './TableIconAction';
import { dataTableHeadClass } from './DataTablePanel';
import { SortableTh, sortItems, useTableSort } from './SortableTh';
import { Button, PlusIcon, Select, TextInput } from './ui';
import { TrashIcon } from './ui/Icons';
import { t } from '../i18n';
import { FinancialType } from '../types';
import { formatMoney } from '../utils/format';
import {
  EditableFinancialLine,
  createEmptyLine,
  sumEditableLines,
} from '../utils/financialLines';

type FinancialLinesEditorProps = {
  lines: EditableFinancialLine[];
  onChange: (lines: EditableFinancialLine[]) => void;
  disabled?: boolean;
};

const lineSortGetters: Record<
  string,
  (line: EditableFinancialLine) => string | number | boolean | null | undefined
> = {
  type: (l) => l.type,
  amount: (l) => l.amount,
  description: (l) => l.description,
};

export function FinancialLinesEditor({
  lines,
  onChange,
  disabled = false,
}: FinancialLinesEditorProps) {
  const labels = t();
  const totals = sumEditableLines(lines);
  const { sort, toggleSort } = useTableSort('type');
  const displayLines = useMemo(
    () => sortItems(lines, sort, lineSortGetters),
    [lines, sort],
  );

  const typeOptions = [
    { value: 'INVOICE', label: labels.financial.invoice },
    { value: 'CASH', label: labels.financial.cash },
  ];

  function updateLine(
    key: string,
    patch: Partial<EditableFinancialLine>,
  ) {
    onChange(
      lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
  }

  function removeLine(key: string) {
    onChange(lines.filter((line) => line.key !== key));
  }

  function addLine() {
    onChange([...lines, createEmptyLine()]);
  }

  return (
    <div className="space-y-3">
      <div className="max-h-64 overflow-auto border border-line">
        <table className="min-w-full text-sm">
          <thead className={dataTableHeadClass}>
            <tr>
              <th className="px-3 py-2 font-medium w-24">{labels.common.actions}</th>
              <SortableTh sortKey="type" sort={sort} onSort={toggleSort}>
                {labels.financial.type}
              </SortableTh>
              <SortableTh sortKey="amount" sort={sort} onSort={toggleSort}>
                {labels.financial.amount}
              </SortableTh>
              <SortableTh
                sortKey="description"
                sort={sort}
                onSort={toggleSort}
              >
                {labels.financial.description}
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-ink-muted">
                  Δεν υπάρχουν γραμμές. Προσθέστε γραμμή συμφωνίας.
                </td>
              </tr>
            ) : (
              displayLines.map((line) => (
                <tr key={line.key} className="border-t border-line">
                  <td className="px-3 py-2">
                    <TableIconButton
                      label={labels.financial.removeLine}
                      variant="muted"
                      disabled={disabled}
                      onClick={() => removeLine(line.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </TableIconButton>
                  </td>
                  <td className="px-3 py-2">
                    <Select
                      value={line.type}
                      disabled={disabled}
                      options={typeOptions}
                      showClearButton={false}
                      size="sm"
                      onChange={(e) =>
                        updateLine(line.key, {
                          type: e.target.value as FinancialType,
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <TextInput
                      type="text"
                      inputMode="decimal"
                      disabled={disabled}
                      value={line.amount}
                      onChange={(e) =>
                        updateLine(line.key, { amount: e.target.value })
                      }
                      placeholder={t().common.placeholders.amount}
                      showClearButton={false}
                      size="sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <TextInput
                      type="text"
                      disabled={disabled}
                      value={line.description}
                      onChange={(e) =>
                        updateLine(line.key, { description: e.target.value })
                      }
                      showClearButton={false}
                      placeholder=""
                      size="sm"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={addLine}
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {labels.financial.addLine}
        </Button>

        <div className="space-y-1 text-right text-sm">
          <div>
            {labels.financial.invoiceTotal}:{' '}
            <span className="font-medium">{formatMoney(totals.invoice)}</span>
          </div>
          <div>
            {labels.financial.cashTotal}:{' '}
            <span className="font-medium">{formatMoney(totals.cash)}</span>
          </div>
          <div>
            {labels.financial.grandTotal}:{' '}
            <span className="font-semibold">{formatMoney(totals.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
